import React from 'react';
import { Col, Container, Row } from 'reactstrap';
import { Link } from 'react-router-dom';

/**
 * How it works, and what "bring your own template" actually means.
 *
 * Images removed for now — the old ones were charts of our own model output,
 * which illustrated a product we no longer describe. Screenshots of the wizard
 * and a scorecard belong here once they exist.
 *
 * The output contract is shown literally rather than described. Someone deciding
 * whether their own prompt would fit needs to see the shape, not be told there
 * is one.
 */

const STEPS = [
    {
        n: '1',
        title: 'Ask',
        body: `Pick a symbol and a horizon — that is the whole form. We build the prompt,
               the same structure every time, stating exactly what to answer and in what
               shape. We do not tell the model what the asset is trading at: finding that
               out is part of the task, and it is the clearest sign of whether a model
               looked anything up.`,
    },
    {
        n: '2',
        title: 'Record',
        body: `Paste the answer back and say which model wrote it. The window runs from the
               moment the prompt was built, so a forecast cannot be backdated into a period
               whose outcome is already known — and once saved it cannot be edited.`,
    },
    {
        n: '3',
        title: 'Settle',
        body: `When the horizon closes you enter what the asset actually did — or read it
               off a chart, using the TradingView script we generate for you — and every
               model that answered is settled together. Did the close land inside the stated
               range? Do the confidence numbers hold up? Which of your models is doing
               better?`,
    },
];

/**
 * What an answer has to look like, for somebody bringing their own prompt.
 *
 * Kept in step with backend/services/promptBuilder.js by hand, and it had
 * drifted: the horizons were wrong (a "1h" that is never asked for, no "180d"
 * that is), and it was missing both data_access and current_price. Somebody
 * writing to this would have produced answers that parse but score as though
 * the model never priced the asset.
 *
 * model and answered_at_utc are the model's own account of which model it is
 * and when it wrote. Neither is verifiable and neither is scored; they exist so
 * an answer filed under the wrong model can be spotted, which nothing else in
 * the record can do. "unknown" is an accepted answer for the timestamp.
 */
const OUTPUT_SHAPE = `{
  "signals": {
    "4h":   "BULLISH | MILD BULLISH | NEUTRAL | MILD BEARISH | BEARISH",
    "1d":   "...",
    "7d":   "...",
    "30d":  "...",
    "180d": "..."
  },
  "current_price": "$214.30",
  "price_range": "$210.00–$219.50",
  "confidence": "58%",
  "data_access": "live | partial | stale",
  "model": "GPT-5.1",
  "answered_at_utc": "2026-09-01T14:32:00Z",
  "rationale": "why, in the model's own words"
}`;

const Features = () => {
    return (
        <React.Fragment>

            {/* ── How it works ───────────────────────────────────────────── */}
            <section className="section" id="how-it-works">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8}>
                            <div className="text-center mb-5">
                                <h1 className="mb-3 ff-secondary fw-semibold lh-base">Three steps, then it keeps itself</h1>
                                <p className="text-muted">
                                    The work is asking well and remembering honestly. Both of those are
                                    ours; the analysis stays yours.
                                </p>
                            </div>
                        </Col>
                    </Row>

                    <Row className="g-4 justify-content-center">
                        {STEPS.map((step) => (
                            <Col lg={4} md={6} key={step.n}>
                                <div className="p-4 h-100 border rounded">
                                    <div className="avatar-sm mb-3">
                                        <div className="avatar-title bg-success-subtle text-success rounded-circle fs-20 fw-semibold">
                                            {step.n}
                                        </div>
                                    </div>
                                    <h5 className="fs-18 mb-2">{step.title}</h5>
                                    <p className="text-muted mb-0 ff-secondary">{step.body}</p>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* ── Your own template ──────────────────────────────────────── */}
            <section className="section bg-light" id="your-template">
                <Container>
                    <Row className="align-items-center gy-4">
                        <Col lg={6}>
                            <div className="text-muted">
                                <h5 className="fs-12 text-uppercase text-success">Your method</h5>
                                <h4 className="mb-3">Use your own prompt if you prefer</h4>
                                <p className="mb-3 ff-secondary fs-16">
                                    Our templates are a starting point, not a requirement. If you have
                                    a way of asking that works for you, keep it — the only thing that
                                    has to match is the shape of the answer.
                                </p>
                                <p className="mb-4 ff-secondary fs-16">
                                    Anything that comes back in this format is parsed, stored and
                                    scored exactly like every other forecast, and lines up beside them
                                    in your history. Do include <code>current_price</code>: we never
                                    tell the model what the asset is worth, and how close its own
                                    figure lands is part of the score.
                                </p>

                                <div className="vstack gap-2">
                                    {[
                                        'The price the model found for itself — it is scored on that',
                                        'A price range in currency, so coverage can be measured',
                                        'A confidence number, so calibration can be measured',
                                        'A direction per horizon — NEUTRAL is a valid answer',
                                    ].map((line) => (
                                        <div className="d-flex align-items-center" key={line}>
                                            <div className="flex-shrink-0 me-2">
                                                <div className="avatar-xs icon-effect">
                                                    <div className="avatar-title bg-transparent text-success rounded-circle h2">
                                                        <i className="ri-check-fill"></i>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex-grow-1">
                                                <p className="mb-0">{line}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Col>

                        <Col lg={6}>
                            <div className="p-3 bg-body border rounded">
                                <div className="text-muted fs-12 mb-2">Expected output</div>
                                <pre className="mb-0 fs-12 text-muted"
                                     style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {OUTPUT_SHAPE}
                                </pre>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* ── What gets measured ─────────────────────────────────────── */}
            <section className="section" id="what-is-measured">
                <Container>
                    <Row className="align-items-center gy-4">
                        <Col lg={7}>
                            <div className="text-muted">
                                <h5 className="fs-12 text-uppercase text-success">Scoring</h5>
                                <h4 className="mb-3">Measured in the order the numbers can be trusted</h4>
                                <p className="mb-4 ff-secondary fs-16">
                                    Single-period direction is close to a coin flip for everyone, and we
                                    will not pretend otherwise. The measures that carry real information
                                    come first.
                                </p>

                                <div className="vstack gap-3">
                                    <div>
                                        <h5 className="fs-15 mb-1">Range coverage</h5>
                                        <p className="mb-0 fs-14">
                                            How often the close actually landed inside the stated range.
                                            Unambiguous, and the number worth quoting.
                                        </p>
                                    </div>
                                    <div>
                                        <h5 className="fs-15 mb-1">Confidence calibration</h5>
                                        <p className="mb-0 fs-14">
                                            Do the forecasts labelled 70% come true about 70% of the time?
                                            Almost nobody measures this, and it is what separates a useful
                                            forecaster from a confident one.
                                        </p>
                                    </div>
                                    <div>
                                        <h5 className="fs-15 mb-1">Direction accuracy</h5>
                                        <p className="mb-0 fs-14">
                                            Reported last, and honestly. Abstentions are recorded as
                                            abstentions rather than counted as misses, so an AI that says
                                            "not enough evidence" is not punished for being right about
                                            that.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </Col>

                        <Col lg={5}>
                            <div className="p-4 border rounded bg-light">
                                <p className="text-muted mb-2 fs-14">
                                    Models keep improving. The point of keeping a record is that when
                                    yours gets better, you will be able to show it — and when it does
                                    not, you will know that too.
                                </p>
                                <p className="text-muted mb-0 fs-14">
                                    Nothing here is investment advice. It is your research, recorded
                                    and measured.
                                </p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* ── CTA band ───────────────────────────────────────────────── */}
            <section className="py-5 bg-primary position-relative">
                <div className="bg-overlay bg-overlay-pattern opacity-50"></div>
                <Container>
                    <Row className="align-items-center gy-3">
                        <Col className="col-sm">
                            <h4 className="text-white mb-0 fw-semibold">
                                Start keeping a record of what your AI actually said
                            </h4>
                        </Col>
                        <Col className="col-sm-auto">
                            <Link to="/register" className="btn bg-gradient btn-light">
                                Start free <i className="ri-arrow-right-line align-middle ms-1"></i>
                            </Link>
                        </Col>
                    </Row>
                </Container>
            </section>

        </React.Fragment>
    );
};

export default Features;
